import { createContext, useContext, useState, useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import classNames from "classnames";

import checkMarkIcon from "/assets/check-mark.svg";
import crossIcon from "/assets/close-white.svg";
import styles from "./toast.module.css";

const ToastContext = createContext();

// TOAST_FADE_MS mirrors .toastLeaving in toast.module.css.
const TOAST_LIFETIME_MS = 6000;
const TOAST_FADE_MS = 500;
const TOAST_SETTLE_MS = 250;

const KINDS = {
  success: { icon: checkMarkIcon, title: "успех" },
  error: { icon: crossIcon, title: "ошибка" },
};

export function Toast({
  type,
  message,
  leaving,
}) {
  const { icon, title } = KINDS[type] ?? KINDS.error;

  return (
    // Separate elements so the slot's transform and the toast's fade never share
    // an inline `transition`.
    <div className={styles.toastSlot}>
      <div
        role="status"
        className={classNames(styles.toast, {
          [styles.toastSuccess]: type === "success",
          [styles.toastError]: type !== "success",
          [styles.toastLeaving]: leaving,
        })}
      >
        <div className={styles.toastIcon}>
          <img src={icon} alt="" />
        </div>

        <div>
          <p className={styles.toastTitle}>{title}</p>
          <p>{message}</p>
        </div>
      </div>
    </div>
  );
}

const clearGlide = (el) => {
  el.style.transition = "";
  el.style.transform = "";
};

// FLIP, as in useReorderAnimation: the slots below a removed toast glide up
// instead of jumping, so the toast can fade out at full size. `remove` runs
// inside because positions must be read either side of the same paint.
const glideStackClosed = (container, remove) => {
  const slots = Array.from(container?.children ?? []);

  // A slot still mid-glide reports a transformed rect, so land it first.
  slots.forEach(clearGlide);
  const topsBefore = new Map(slots.map((el) => [el, el.getBoundingClientRect().top]));

  flushSync(remove);

  const moved = [];
  topsBefore.forEach((topBefore, el) => {
    if (!el.isConnected) return;

    const shift = topBefore - el.getBoundingClientRect().top;
    if (Math.abs(shift) < 1) return;

    el.style.transition = "none";
    el.style.transform = `translateY(${shift}px)`;
    moved.push(el);
  });

  if (moved.length === 0) return;

  // Commit the inverted positions, or both styles collapse into one paint and
  // nothing animates.
  void document.body.offsetHeight;

  requestAnimationFrame(() => {
    moved.forEach((el) => {
      el.style.transition = `transform ${TOAST_SETTLE_MS}ms ease-out`;
      el.style.transform = "";
    });
  });

  setTimeout(() => moved.forEach(clearGlide), TOAST_SETTLE_MS + 50);
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const containerRef = useRef(null);

  const removeToast = useCallback((id) => {
    glideStackClosed(containerRef.current, () => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    });
  }, []);

  const addToast = useCallback((message, type) => {
    const id = Math.random().toString(36).substring(2, 9); // Simple ID
    const toast = { id, message, type };
    setToasts((prev) => [...prev, toast]);

    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      setTimeout(() => removeToast(id), TOAST_FADE_MS);
    }, TOAST_LIFETIME_MS);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}

      <div className={styles.toastsContainer} ref={containerRef}>
        {toasts.map((toast) => (
          <Toast key={toast.id} type={toast.type} message={toast.message} leaving={toast.leaving} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return {
    successToast: (msg) => context.addToast(msg, 'success'),
    errorToast: (msg) => context.addToast(msg, 'error'),
  };
}
